/**
 * Marketing-page shell — Nav + Footer pulled out so /compare and per-rival
 * pages share the same chrome as Landing without duplicating the markup.
 */
import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Building2, Compass, Menu, X } from "lucide-react";
import AssignFlowLogo from "@/components/AssignFlowLogo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/compare", label: "Compare" },
  { href: "/#pricing", label: "Pricing" },
  { href: "/#faqs", label: "FAQs" },
];

export default function MarketingShell({ children, active }) {
  return (
    <div className="mkt-shell">
      <Nav active={active} />
      {children}
      <Footer />
    </div>
  );
}

function Nav({ active }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="mkt-nav">
      <div className="mkt-nav-inner">
        <Link to="/" className="flex items-center" data-testid="mkt-brand">
          <img
            src="/brand/wordmark-color.png"
            alt="iSpeedToClose"
            className="h-14 md:h-16 w-auto"
            width="243"
            height="64"
          />
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-[var(--ink-2)]">
          {LINKS.map((l) => {
            const isActive = active === l.label;
            const cls = "hover:text-[var(--ink)] " + (isActive ? "text-[var(--ink)] font-semibold" : "");
            return l.to ? (
              <Link key={l.label} to={l.to} className={cls} data-testid={`mkt-nav-${l.label.toLowerCase()}`}>
                {l.label}
              </Link>
            ) : (
              <a key={l.label} href={l.href} className={cls} data-testid={`mkt-nav-${l.label.toLowerCase()}`}>
                {l.label}
              </a>
            );
          })}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" className="btn-ghost" data-testid="mkt-login-button">Sign in</Link>
          <Link to="/register" className="btn-money" data-testid="mkt-cta-register">
            Try free <ArrowRight size={14} />
          </Link>
        </div>
        <button
          type="button"
          className="md:hidden p-2 rounded-lg hover:bg-black/5"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mkt-mobile-menu-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-black/5 bg-white">
          <nav className="px-6 py-4 flex flex-col gap-3 text-sm font-medium text-[var(--ink)]">
            {LINKS.map((l) =>
              l.to ? (
                <Link key={l.label} to={l.to} className="py-1.5" onClick={() => setOpen(false)}>
                  {l.label}
                </Link>
              ) : (
                <a key={l.label} href={l.href} className="py-1.5" onClick={() => setOpen(false)}>
                  {l.label}
                </a>
              ),
            )}
            <div className="flex flex-col gap-2 pt-3 border-t border-black/5">
              <Link to="/login" className="btn-ghost" onClick={() => setOpen(false)}>Sign in</Link>
              <Link to="/register" className="btn-money" onClick={() => setOpen(false)}>
                Try free <ArrowRight size={14} />
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[var(--line)] bg-white">
      <div className="max-w-[1240px] mx-auto px-8 py-12 grid md:grid-cols-4 gap-8 text-sm">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="w-7 h-7 rounded-md bg-[var(--ink)] text-white flex items-center justify-center"><AssignFlowLogo size={16} /></span>
            <span className="font-display font-extrabold">iSpeedToClose</span>
          </div>
          <p className="text-[var(--ink-2)] leading-relaxed">Speed is the deal. The all-in-one OS for real estate wholesalers + investors. Built with operators, for operators.</p>
        </div>
        <FooterCol title="Platform" items={[
          { label: "Discover", to: "/v3/crm/discover" },
          { label: "Deal Alerts", to: "/v3/alerts" },
          { label: "Analyzer", to: "/v3/tools/analyzer" },
          { label: "Pipeline", to: "/v3/crm/pipeline" },
          { label: "Hot Streak Leaderboard", to: "/leaderboards/streaks" },
          { label: "Refer & Earn", to: "/register" },
        ]} />
        <FooterCol title="Company" items={[
          { label: "Compare", to: "/compare" },
          { label: "Pricing", href: "/#pricing" },
          { label: "FAQs", href: "/#faqs" },
          { label: "Sign in", to: "/login" },
        ]} />
        <FooterCol title="Legal" items={[
          { label: "Terms", to: "/legal/terms" },
          { label: "Privacy", to: "/legal/privacy" },
          { label: "Contact", href: "mailto:hello@ispeedtoclose.com" },
        ]} />
      </div>
      <div className="border-t border-[var(--line)]">
        <div className="max-w-[1240px] mx-auto px-8 py-6 text-xs text-[var(--ink-2)] flex flex-wrap items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} iSpeedToClose · A Perrin Properties, LLC product.</div>
          <div className="flex items-center gap-1"><Compass size={11} /> Built with care in the field.</div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <div className="font-display font-bold mb-3">{title}</div>
      <ul className="space-y-2 text-[var(--ink-2)]">
        {items.map((i) => (
          <li key={i.label}>
            {i.to ? (
              <Link to={i.to} className="hover:text-[var(--ink)] transition-colors">{i.label}</Link>
            ) : (
              <a href={i.href} className="hover:text-[var(--ink)] transition-colors">{i.label}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
